import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Mail } from 'lucide-react';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import type { MsrPurchaseOrderEmail } from '~/api/order';
import { Button } from '~/components/ui';
import { EmailMsrPurchaseOrderModal } from '~/pages/order-view/sections/EmailMsrPurchaseOrderModal';
import { countsAsEmailed } from '~/pages/order-view/sections/purchaseOrderEmails';
import { formatDateTime } from '~/utils';
import { contactLabel } from '~/utils/contact';
import {
  msrPurchaseOrderPdfUrl,
  type MsrPurchaseOrderPrintOptions,
} from '~/utils/msrPurchaseOrderUrl';

export function MsrPurchaseOrderModal({
  isOpen,
  onClose,
  orderId,
  printOptions,
  purchaseOrderNumber,
  sellingOrganizationId,
  sellerContactIds,
  purchaseOrderEmails,
}: Readonly<{
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  printOptions: MsrPurchaseOrderPrintOptions;
  purchaseOrderNumber: string;
  sellingOrganizationId: string;
  sellerContactIds: string[];
  purchaseOrderEmails: MsrPurchaseOrderEmail[];
}>) {
  const { t } = useTranslation('order');
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const lastEmail = purchaseOrderEmails.find(countsAsEmailed);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div
        className="bg-neut-bg-alt rounded-lg w-[min(56rem,calc(100vw-2rem))] flex flex-col"
        data-testid="msr-purchase-order-modal"
      >
        <ModalHeader
          title={t('order.purchase_order_modal.title', { purchaseOrderNumber })}
          onClose={onClose}
        />
        <iframe
          src={msrPurchaseOrderPdfUrl(orderId, { printOptions })}
          title={t('order.purchase_order_modal.pdf_title', {
            purchaseOrderNumber,
          })}
          className="w-full h-[70vh] border-b border-neut-200"
          data-testid="purchase-order-pdf"
        />
        <div className="flex flex-wrap items-center justify-end gap-3 p-4">
          {lastEmail && (
            <p
              className="mr-auto text-sm text-neut-sec"
              data-testid="purchase-order-last-emailed"
            >
              {lastEmail.requestedBy
                ? t('order.purchase_order_modal.last_emailed_by', {
                    date: formatDateTime(lastEmail.createdAt),
                    name: contactLabel(lastEmail.requestedBy),
                  })
                : t('order.purchase_order_modal.last_emailed', {
                    date: formatDateTime(lastEmail.createdAt),
                  })}
            </p>
          )}
          <Button
            size="sm"
            width="auto"
            variant="secondary"
            onClick={() => setIsEmailModalOpen(true)}
            data-testid="email-purchase-order-button"
          >
            <Mail size={16} aria-hidden="true" />
            {t('order.purchase_order_modal.email_button')}
          </Button>
          <Button
            size="sm"
            width="auto"
            onClick={() =>
              window.open(
                msrPurchaseOrderPdfUrl(orderId, {
                  download: true,
                  printOptions,
                }),
                '_blank',
                'noopener,noreferrer'
              )
            }
            data-testid="download-purchase-order-button"
          >
            <Download size={16} aria-hidden="true" />
            {t('order.purchase_order_modal.download_button')}
          </Button>
        </div>
      </div>
      {isEmailModalOpen && (
        <EmailMsrPurchaseOrderModal
          onClose={() => setIsEmailModalOpen(false)}
          orderId={orderId}
          printOptions={printOptions}
          purchaseOrderNumber={purchaseOrderNumber}
          sellingOrganizationId={sellingOrganizationId}
          initialContactIds={sellerContactIds}
          purchaseOrderEmails={purchaseOrderEmails}
        />
      )}
    </Modal>
  );
}
